// Discover's filters (the app's FeedFilters, lib/blocs/feed_deck/
// feed_filters.dart): a tempo range, a scale and keys, tags (the blurb's
// #hashtags) and live jams only. Sent to GET /feed as bpm_min, bpm_max,
// key, scale, tags and live_only (B3), and applied to the cards the deck
// holds as well, so a server that ignores them still shows what the chips
// say. A filters value is a plain object:
//   { bpmMin, bpmMax, scale: "any" | "major" | "minor", keys: [...],
//     tags: [...], liveOnly }
import { displayKey } from "./musicKey.js";
import { sessionTags } from "./sessionTags.js";
import { sessionIsLive } from "./sessionRow.js";

export const TEMPO_FLOOR = 60;
export const TEMPO_CEILING = 180;

// The key grid, in the sheet's order and spelling.
export const KEY_NAMES = ["C", "C♯", "D", "E♭", "E", "F", "F♯", "G", "A♭", "A", "B♭", "B"];

// The tags offered before any come back from the feed.
export const SUGGESTED_TAGS = ["lofi", "keys", "drums", "ambient", "guitar", "vocals", "bass"];

export const SCALES = [
  { value: "any", label: "Any" },
  { value: "major", label: "Major" },
  { value: "minor", label: "Minor" },
];

// GET /feed?with_total=1 counts matches among the newest 500 public
// sessions (flask feed_filters.CANDIDATE_CAP), so a total at the cap means
// "at least 500" (L9).
export const FEED_TOTAL_CAP = 500;

// "1 jam", "24 jams", "500+ jams".
export function jamsText(total) {
  const n = Number(total);
  if (n >= FEED_TOTAL_CAP) return `${FEED_TOTAL_CAP}+ jams`;
  return `${n} ${n === 1 ? "jam" : "jams"}`;
}

export function emptyFilters() {
  return { bpmMin: null, bpmMax: null, scale: "any", keys: [], tags: [], liveOnly: false };
}

export function normalize(f) {
  const base = emptyFilters();
  if (!f || typeof f !== "object") return base;
  const n = (v) => (v === null || v === undefined || v === "" || !Number.isFinite(Number(v)) ? null : Number(v));
  const keys = [...new Set((f.keys || []).map((k) => KEY_NAMES[pitchClass(k)]).filter(Boolean))];
  keys.sort((a, b) => KEY_NAMES.indexOf(a) - KEY_NAMES.indexOf(b));
  const tags = [...new Set((f.tags || []).map((t) => String(t).replace(/^#/, "").trim().toLowerCase()).filter(Boolean))];
  return {
    bpmMin: n(f.bpmMin),
    bpmMax: n(f.bpmMax),
    scale: ["major", "minor"].includes(f.scale) ? f.scale : "any",
    keys,
    tags,
    liveOnly: !!f.liveOnly,
  };
}

export const hasTempo = (f) => f.bpmMin != null || f.bpmMax != null;
export const hasKey = (f) => f.scale !== "any" || f.keys.length > 0;
export const isEmpty = (f) => !hasTempo(f) && !hasKey(f) && f.tags.length === 0 && !f.liveOnly;

export function sameFilters(a, b) {
  const x = normalize(a);
  const y = normalize(b);
  return (
    x.bpmMin === y.bpmMin &&
    x.bpmMax === y.bpmMax &&
    x.scale === y.scale &&
    x.liveOnly === y.liveOnly &&
    [...x.keys].sort().join() === [...y.keys].sort().join() &&
    [...x.tags].sort().join() === [...y.tags].sort().join()
  );
}

// "90 to 110 BPM": the chip and the sheet's readout.
export function tempoLabel(f) {
  const lo = Math.round(f.bpmMin ?? TEMPO_FLOOR);
  const hi = Math.round(f.bpmMax ?? TEMPO_CEILING);
  return `${lo} to ${hi} BPM`;
}

// The Key chip: the scale, or the key picked, or "N keys".
export function keyLabel(f) {
  if (f.scale !== "any") return f.scale === "major" ? "Major" : "Minor";
  if (f.keys.length === 1) return f.keys[0];
  if (f.keys.length) return `${f.keys.length} keys`;
  return "Key";
}

// The Tags chip: "#lofi #keys".
export function tagsLabel(f) {
  return f.tags.length ? f.tags.map((t) => `#${t}`).join(" ") : "Tags";
}

// The chips' words, with the caret: "Tempo ▾", "90 to 110 BPM ▾".
export function chipLabels(f) {
  return {
    tempo: hasTempo(f) ? tempoLabel(f) : "Tempo",
    key: keyLabel(f),
    tags: tagsLabel(f),
  };
}

// GET /feed's query (the app's FeedFilters.query). Keys go in the sheet's
// spelling, comma-joined; tags match ANY.
export function toQuery(filters) {
  const f = normalize(filters);
  const q = {};
  if (f.bpmMin != null) q.bpm_min = String(Math.round(f.bpmMin));
  if (f.bpmMax != null) q.bpm_max = String(Math.round(f.bpmMax));
  if (f.keys.length) q.key = f.keys.join(",");
  if (f.scale !== "any") q.scale = f.scale;
  if (f.tags.length) q.tags = f.tags.join(",");
  if (f.liveOnly) q.live_only = "true";
  return q;
}

const LETTER = { C: 0, D: 2, E: 4, F: 5, G: 7, A: 9, B: 11 };

// "A♭", "Ab", "G#", "g♯m" → its pitch class (0 to 11), or null.
export function pitchClass(name) {
  if (name === null || name === undefined) return null;
  if (typeof name === "number") return Number.isInteger(name) && name >= 0 && name < 12 ? name : null;
  const m = /^\s*([A-Ga-g])\s*([#♯b♭]?)/u.exec(String(name));
  if (!m) return null;
  let pc = LETTER[m[1].toUpperCase()];
  if (m[2] === "#" || m[2] === "♯") pc += 1;
  if (m[2] === "b" || m[2] === "♭") pc -= 1;
  return (pc + 12) % 12;
}

// A session's key as { pc, minor, label } ("Dm", "Ab", "F#m", "musical_key"
// on SQL rows), or null when it has none.
export function sessionKey(row) {
  const raw = row && (row.key || row.musical_key);
  if (typeof raw !== "string" || !raw.trim()) return null;
  const s = raw.trim();
  const pc = pitchClass(s);
  if (pc === null) return null;
  const rest = s.replace(/^\s*[A-Ga-g]\s*[#♯b♭]?/u, "").trim().toLowerCase();
  const minor = rest === "m" || rest.startsWith("min") || rest === "-";
  return { pc, minor, label: displayKey(s.replace(/\s*(minor|min)$/i, "m").replace(/\s*(major|maj)$/i, "")) };
}

// The session's tempo: tempo_bpm, or bpm.
export function sessionBpm(row) {
  const v = row && (row.tempo_bpm ?? row.bpm);
  const n = Number(v);
  return v != null && v !== "" && Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * Whether `row` passes `filters`. A session without a tempo or a key
 * passes those filters rather than vanishing (servers that don't store
 * them yet); tags match ANY.
 */
export function matches(row, filters, { me = null, now } = {}) {
  const f = normalize(filters);
  if (f.liveOnly && !rowIsLive(row, me, now)) return false;
  const bpm = sessionBpm(row);
  if (bpm != null) {
    if (f.bpmMin != null && bpm < f.bpmMin) return false;
    if (f.bpmMax != null && bpm > f.bpmMax) return false;
  }
  const key = sessionKey(row);
  if (key) {
    if (f.scale === "major" && key.minor) return false;
    if (f.scale === "minor" && !key.minor) return false;
    if (f.keys.length && !f.keys.some((k) => pitchClass(k) === key.pc)) return false;
  }
  if (f.tags.length) {
    const own = sessionTags(row);
    if (!f.tags.some((t) => own.includes(t))) return false;
  }
  return true;
}

// Live for the deck: the server's `live` (B1) when sent, else a fresh
// heartbeat from someone other than you (SI-22).
export function rowIsLive(row, me, now) {
  if (!row) return false;
  if (typeof row.live === "boolean") return row.live;
  return sessionIsLive(row, me, now);
}
